/**
 * PRIVACY: Geen user-input naar server, localStorage (behalve onboarding + UI-voorkeuren),
 * externe APIs. PortalAuth verify via SDK; minimale Umami analytics (tool_open/close).
 */
import { useCallback, useEffect, useRef, useState, lazy, Suspense } from "react";
import {
  ToolLayout,
  ToolTabs,
  PaywallModal,
  Onboarding,
  useAccess,
  hasCompletedOnboarding,
  setOnboardingComplete,
  formatPrice,
  DEFAULT_TOOL_PRICE_INCL,
  type OnboardingSlide,
} from "@jari-tools/shared";
import { RemoteApp } from "./RemoteApp";
import { InspiratiePanel } from "./components/InspiratiePanel";
import bodyHtml from "./emdr-body.html?raw";
import "./design-colors.css";
import "./style.css";
import "./figures.css";
import "./inspiratie.css";
import "./tool.css";
import "./racebaan.css";
import "./danspas.css";
import { EMDR_HANDLEIDING_ENABLED, EMDR_KINDEREN_ENABLED } from "./lib/feature-flags";
import { useEmdrActivityTracking } from "./hooks/useEmdrActivityTracking";

if (EMDR_HANDLEIDING_ENABLED) {
  void import("./handleiding.css");
}

const HandleidingPanel = EMDR_HANDLEIDING_ENABLED
  ? lazy(() =>
      import("./components/HandleidingPanel").then((m) => ({
        default: m.HandleidingPanel,
      }))
    )
  : null;

type MainTab = "uitvoeren" | "uitvoeren-kinderen" | "inspiratie";

const TOOL_SLUG = import.meta.env.VITE_TOOL_SLUG || "emdr";
const TOOL_NAME = "EMDR Toolkit";
const TOOL_SUBTITLE = "Met extra werkgeheugenbelasting opties, te bedienen met je mobiel!";
const PORTAL_URL = import.meta.env.VITE_PORTAL_URL;

const ONBOARDING_SLIDES: OnboardingSlide[] = [
  {
    title: "Werkgeheugenbelasting",
    body: "Deze tool biedt verschillende vormen van werkgeheugenbelasting die ingezet kunnen worden bij EMDR. De tool biedt zowel ingebouwde visuele en auditieve werkgeheugenbelastingstaken als een databank met inspiratie voor allerlei andere soorten werkgeheugenbelasting.",
  },
  {
    title: "Werkgeheugenbelasting instellen",
    body: "Jij stelt de snelheid, het patroon en eventuele extra taken gemakkelijk tijdens een sessie in. Je kunt bij de verschillende taken kiezen voor vier moeilijkheidsniveaus, zodat ze bruikbaar zijn van kind tot volwassene.",
  },
  {
    title: "Volledig scherm en bediening",
    body: "Tijdens de EMDR sessie zet je het scherm op volledig scherm en kun je de tool bedienen met je telefoon.",
  },
  {
    title: "Doorontwikkeling",
    body: "Deze EMDR toolkit is al goed te gebruiken en tegelijkertijd blijven we hem doorontwikkelen. Zo zijn we aan het werk om een video-bel-modus toe te voegen, zodat je op afstand EMDR kunt geven zonder haperingen!",
  },
];

export function App() {
  const remoteParam = new URLSearchParams(location.search).get("remote");
  if (remoteParam !== null) {
    return <RemoteApp initialCode={remoteParam || null} />;
  }
  return <DesktopApp />;
}

function DesktopApp() {
  const { access, name, loading, error, errorCode, isPaid, isGuest, toolId, toolPriceAmount } =
    useAccess();
  const hostRef = useRef<HTMLDivElement>(null);
  const bootedRef = useRef(false);
  const [booted, setBooted] = useState(false);
  const latestIsPaidRef = useRef(isPaid);
  const [showOnboarding, setShowOnboarding] = useState(
    () => !hasCompletedOnboarding(TOOL_SLUG)
  );
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallFeature, setPaywallFeature] = useState("deze functie");
  const [paywallReason, setPaywallReason] = useState<"license" | "trial">("license");
  const [mainTab, setMainTab] = useState<MainTab>("uitvoeren");
  const [handleidingOpen, setHandleidingOpen] = useState(false);
  const [remoteLocked, setRemoteLocked] = useState(false);
  const currentAudience: "volwassenen" | "kinderen" =
    EMDR_KINDEREN_ENABLED && mainTab === "uitvoeren-kinderen"
      ? "kinderen"
      : "volwassenen";
  const latestAudienceRef = useRef(currentAudience);

  latestIsPaidRef.current = isPaid;
  latestAudienceRef.current = currentAudience;

  useEmdrActivityTracking(booted);

  const completeOnboarding = useCallback(() => {
    setOnboardingComplete(TOOL_SLUG);
    setShowOnboarding(false);
  }, []);

  const onPaywall = useCallback((feature: string, reason: "license" | "trial" = "license") => {
    setPaywallFeature(feature);
    setPaywallReason(reason);
    setPaywallOpen(true);
  }, []);

  useEffect(() => {
    if (loading || !hostRef.current) return;

    window.__EMDR_IS_PAID__ = latestIsPaidRef.current;

    if (!bootedRef.current) {
      let cancelled = false;
      hostRef.current.innerHTML = bodyHtml;
      bootedRef.current = true;
      import("./engine/bootstrap").then(({ bootstrapEmdr }) =>
        bootstrapEmdr({ isPaid: latestIsPaidRef.current, onPaywall }).then(() => {
          if (cancelled) return;
          window.__EMDR_IS_PAID__ = latestIsPaidRef.current;
          window.__EMDR_REFRESH_PAID_UI__?.();
          window.__EMDR_SET_AUDIENCE__?.(latestAudienceRef.current);
          if (!cancelled) setBooted(true);
        })
      );
      return () => {
        cancelled = true;
      };
    }

    window.__EMDR_REFRESH_PAID_UI__?.();
  }, [loading, onPaywall, isPaid]);

  useEffect(() => {
    if (!bootedRef.current) return;
    window.__EMDR_SET_AUDIENCE__?.(currentAudience);
  }, [currentAudience]);

  useEffect(() => {
    const syncRemoteLock = () => setRemoteLocked(!!window.__EMDR_DESKTOP_LOCKED__);
    window.addEventListener("emdr-remote-lock-change", syncRemoteLock);
    syncRemoteLock();
    return () => window.removeEventListener("emdr-remote-lock-change", syncRemoteLock);
  }, []);

  const isUitvoeren =
    mainTab === "uitvoeren" ||
    (EMDR_KINDEREN_ENABLED && mainTab === "uitvoeren-kinderen");

  return (
    <>
      <ToolLayout
        toolName={TOOL_NAME}
        toolSubtitle={TOOL_SUBTITLE}
        access={access}
        userName={name}
        loading={loading}
        error={error}
        errorCode={errorCode}
        portalUrl={PORTAL_URL}
        isGuest={isGuest}
        toolId={toolId}
        toolSlug={TOOL_SLUG}
        onShowOnboarding={() => setShowOnboarding(true)}
        headerExtraActions={
          EMDR_HANDLEIDING_ENABLED ? (
            <button
              type="button"
              className="jari-btn"
              onClick={() => setHandleidingOpen(true)}
              title="Handleiding mobiele bediening"
            >
              Handleiding
            </button>
          ) : undefined
        }
      >
        <ToolTabs
          ariaLabel="EMDR navigatie"
          activeId={mainTab}
          onSelect={(id) => setMainTab(id as MainTab)}
          tabIdPrefix="emdr-tab"
          panelIdPrefix="emdr-panel"
          tabs={[
            {
              id: "uitvoeren",
              label: "EMDR uitvoeren",
              disabled: remoteLocked && mainTab !== "uitvoeren",
            },
            ...(EMDR_KINDEREN_ENABLED
              ? [
                  {
                    id: "uitvoeren-kinderen",
                    label: "EMDR kindmodus",
                    disabled: remoteLocked && mainTab !== "uitvoeren-kinderen",
                  },
                ]
              : []),
            {
              id: "inspiratie",
              label: "Inspiratie werkgeheugenbelasting",
              disabled: remoteLocked && mainTab !== "inspiratie",
            },
          ]}
        />

        <div
          id="emdr-panel-uitvoeren"
          role="tabpanel"
          aria-labelledby={
            EMDR_KINDEREN_ENABLED && mainTab === "uitvoeren-kinderen"
              ? "emdr-tab-uitvoeren-kinderen"
              : "emdr-tab-uitvoeren"
          }
          className={`emdr-host jari-tool-emdr${!isUitvoeren ? " emdr-host--hidden" : ""}`}
          ref={hostRef}
          hidden={!isUitvoeren}
        />

        {mainTab === "inspiratie" && (
          <div
            id="emdr-panel-inspiratie"
            role="tabpanel"
            aria-labelledby="emdr-tab-inspiratie"
          >
            <InspiratiePanel />
          </div>
        )}

      </ToolLayout>

      {EMDR_HANDLEIDING_ENABLED && handleidingOpen && HandleidingPanel && (
        <div
          className="emdr-handleiding-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="emdr-handleiding-modal-title"
          onClick={() => setHandleidingOpen(false)}
        >
          <div
            className="emdr-handleiding-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="emdr-handleiding-modal__header">
              <h2 id="emdr-handleiding-modal-title" className="emdr-handleiding-modal__title">
                Handleiding
              </h2>
              <button
                type="button"
                className="jari-btn"
                onClick={() => setHandleidingOpen(false)}
              >
                Sluiten
              </button>
            </div>
            <div className="emdr-handleiding-modal__body">
              <Suspense fallback={null}>
                <HandleidingPanel />
              </Suspense>
            </div>
          </div>
        </div>
      )}

      <Onboarding
        open={showOnboarding}
        slides={ONBOARDING_SLIDES}
        onComplete={completeOnboarding}
        onSkip={completeOnboarding}
      />

      <PaywallModal
        open={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        featureTitle={paywallFeature}
        portalUrl={PORTAL_URL}
        toolSlug={TOOL_SLUG}
        variant={paywallReason}
        priceLabel={formatPrice(toolPriceAmount ?? DEFAULT_TOOL_PRICE_INCL)}
        toolDisplayName={TOOL_NAME}
      />
    </>
  );
}
